import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { IconDefinitionConstants } from "utils/constants";
import { TableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import {
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  StyledButton,
  ViewPanel,
  WidgetArea,
} from "components/common";
import ArticlesList from "./list";
import ArticlesSalesSummaryPieChart from "./widgets/ArticlesSalesSummaryPieChart";
import ArticlesSalesSummaryOverTimeLine from "./widgets/ArticlesSalesSummaryOverTimeLine";
import useCreateArticleDialog from "./forms/useCreateDialog";
import useArticleReportFilterDefinition from "./useReportFilterDefinition";
import { Grid } from "@mui/material";
import useImportArticle from "./useImportArticle";
import { useArticleService } from "serviceHooks";
import { useInvoicePermissions } from "components/invoice";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const ArticlesReport: React.FC = (props) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const articleService = useArticleService();
  const {
    filterFormFields: reportFilterDefinition,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
  } = useArticleReportFilterDefinition();

  const { canCreateInvoices, getIsInvoiceAdmin, canUseInvoices, hasChartsPermissions } = useInvoicePermissions();

  const getCreateDialog = useCreateArticleDialog();
  const importArticleDialog = useImportArticle();
  const onCreateArticleClick = (onSubmitSuccess?: () => void) => {
    dispatch(displayDialog(getCreateDialog(onSubmitSuccess)));
  };

  const onImportArticleClick = async (onSubmitSuccess?: () => void) => {
    dispatch(displayDialog(importArticleDialog(onSubmitSuccess)));
  };

  const tableRef = React.useRef<TableHandles>(null);
  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Articles);

  const exportReportExcel = async () => {
    tableRef.current?.exportRawDataToExcel();
  };

  const exportReportCSV = async () => {
    tableRef.current?.exportRawDataToCSV();
  };

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <PageLayouts.DashboardLayout
          entityKey={0}
          hasPermissions={canUseInvoices}
          title={title}
          topRightButtons={
            <DashboardButtonGroup
              buttons={
                canCreateInvoices
                  ? [
                      <StyledButton size="sm" onClick={onCreateArticleClick.bind(null, reload)}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.CreateArticle)}
                      </StyledButton>,
                      ACTION_SEPARATOR,
                      <FilterButton />,
                    ]
                  : [<FilterButton />]
              }
              moreActions={
                getIsInvoiceAdmin()
                  ? [
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Article.ExportArticleRegisterToEXCEL
                        ),
                        icon: IconDefinitionConstants.WSave,
                        onClickHandler: exportReportExcel,
                      },
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Article.ExportArticleRegisterToCSV
                        ),
                        icon: IconDefinitionConstants.WSave,
                        onClickHandler: exportReportCSV,
                      },
                      {
                        description: LocalizationHelperInstance.getFormattedMessage(
                          TranslationKeys.Article.ImportArticleRegister
                        ),
                        icon: IconDefinitionConstants.WCloudUpload,
                        onClickHandler: onImportArticleClick.bind(null, reload),
                      },
                    ]
                  : []
              }
            />
          }
          filterFields={reportFilterDefinition}
          filterQueryStateHandler={filterQueryStateHandler}
          filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.Article}
          initialFilter={{ active: true }}
          reportToAPIFilterFormatter={articleService.reportToAPIFilter}
          apiToReportFilterFormatter={apiToReportFilterFormatter}
        >
          {hasChartsPermissions() ? (
            <WidgetArea>
              <ViewPanel
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.ArticlesDistribution)}
                appearance="sm"
                gridSize="md"
                isCanvasContent
              >
                <ArticlesSalesSummaryPieChart />
              </ViewPanel>
              <ViewPanel
                title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Article.ArticlesOverTime)}
                appearance="sm"
                gridSize="md"
                isCanvasContent
              >
                <ArticlesSalesSummaryOverTimeLine />
              </ViewPanel>
            </WidgetArea>
          ) : (
            <></>
          )}
          <Grid container item>
            <ArticlesList ref={tableRef} reloadKey={reloadKey} exportFileName={title} />
          </Grid>
        </PageLayouts.DashboardLayout>
      )}
    />
  );
};

export default ArticlesReport;
